import { useEffect, useMemo, useState } from 'react';
import { PageBackButton } from '@/components/common/PageBackButton';
import { toast } from 'react-hot-toast';
import {
  ArrowPathIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  WifiIcon,
  XMarkIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import { useTranslation } from '@/store/languageStore';

type SourceType = 'mailbox' | 'sftp' | 'local';
type SourceStatus = 'success' | 'failed' | null;

interface ImportSource {
  id: number;
  type: SourceType;
  name: string;
  config: string | Record<string, unknown>;
  file_filters?: string | Record<string, unknown> | null;
  retention_days?: number | null;
  is_active: number | boolean;
  last_fetch_at?: string | null;
  last_fetch_status?: SourceStatus;
  last_fetch_error?: string | null;
  last_fetch_files_count?: number | null;
}

interface SourceFormState {
  type: SourceType;
  name: string;
  isActive: boolean;
  retentionDays: string;
  mailboxEmail: string;
  sftpHost: string;
  sftpPort: string;
  sftpUsername: string;
  sftpPassword: string;
  sftpPrivateKeyPath: string;
  sftpPassphrase: string;
  sftpBasePath: string;
  localDirectoryPath: string;
  allowedExtensions: string;
  filenameExclusions: string;
  minDate: string;
  pathPattern: string;
}

const defaultFormState: SourceFormState = {
  type: 'mailbox',
  name: '',
  isActive: true,
  retentionDays: '',
  mailboxEmail: '',
  sftpHost: '',
  sftpPort: '22',
  sftpUsername: '',
  sftpPassword: '',
  sftpPrivateKeyPath: '',
  sftpPassphrase: '',
  sftpBasePath: '',
  localDirectoryPath: '',
  allowedExtensions: '',
  filenameExclusions: '',
  minDate: '',
  pathPattern: '',
};

const ImportSourcesManagement = () => {
  const { t } = useTranslation();
  const [sources, setSources] = useState<ImportSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSourceId, setEditingSourceId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [testingSourceId, setTestingSourceId] = useState<number | null>(null);
  const [syncingSourceId, setSyncingSourceId] = useState<number | null>(null);
  const [deletingSourceId, setDeletingSourceId] = useState<number | null>(null);
  const [form, setForm] = useState<SourceFormState>(defaultFormState);
  const [formError, setFormError] = useState<string | null>(null);

  const editingSource = useMemo(
    () => sources.find((item) => item.id === editingSourceId) ?? null,
    [sources, editingSourceId]
  );

  const parseMaybeJsonObject = (value: unknown): Record<string, unknown> => {
    if (!value) return {};
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>;
        }
      } catch {
        return {};
      }
      return {};
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
    return {};
  };

  const resetForm = () => {
    setForm(defaultFormState);
    setEditingSourceId(null);
    setFormError(null);
  };

  const loadSources = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/import-sources');
      if (response.data?.success) {
        setSources(response.data.data || []);
      } else {
        toast.error(t('sysAdmin.importSources.loadError') || 'Kunde inte ladda importkällor');
      }
    } catch {
      toast.error(t('sysAdmin.importSources.loadError') || 'Kunde inte ladda importkällor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSources();
  }, []);

  const populateFormForEdit = (source: ImportSource) => {
    const config = parseMaybeJsonObject(source.config);
    const fileFilters = parseMaybeJsonObject(source.file_filters);
    setForm({
      type: source.type,
      name: source.name || '',
      isActive: source.is_active === 1 || source.is_active === true,
      retentionDays: source.retention_days != null ? String(source.retention_days) : '',
      mailboxEmail: String(config.mailbox_email || ''),
      sftpHost: String(config.host || ''),
      sftpPort: config.port != null ? String(config.port) : '22',
      sftpUsername: String(config.username || ''),
      sftpPassword: String(config.password || ''),
      sftpPrivateKeyPath: String(config.private_key_path || ''),
      sftpPassphrase: String(config.passphrase || ''),
      sftpBasePath: String(config.base_path || ''),
      localDirectoryPath: String(config.directory_path || ''),
      allowedExtensions: Array.isArray(fileFilters.allowed_extensions)
        ? fileFilters.allowed_extensions.join(', ')
        : '',
      filenameExclusions: Array.isArray(fileFilters.filename_exclusions)
        ? fileFilters.filename_exclusions.join(', ')
        : '',
      minDate: String(fileFilters.min_date || ''),
      pathPattern: String(fileFilters.path_pattern || ''),
    });
    setEditingSourceId(source.id);
    setFormError(null);
    setShowForm(true);
  };

  const buildPayload = () => {
    const type = form.type;
    const name = form.name.trim();
    if (!name) {
      return { error: t('sysAdmin.importSources.validation.nameRequired') || 'Namn krävs' };
    }

    if (type === 'mailbox' && !form.mailboxEmail.trim()) {
      return {
        error:
          t('sysAdmin.importSources.validation.mailboxEmailRequired') ||
          'Mailbox-adress krävs för mailbox-källa',
      };
    }

    if (type === 'sftp') {
      if (!form.sftpHost.trim() || !form.sftpUsername.trim() || !form.sftpBasePath.trim()) {
        return {
          error:
            t('sysAdmin.importSources.validation.sftpRequired') ||
            'Host, användarnamn och base path krävs för SFTP-källa',
        };
      }
    }

    if (type === 'local' && !form.localDirectoryPath.trim()) {
      return {
        error:
          t('sysAdmin.importSources.validation.localDirectoryRequired') ||
          'Katalogsökväg krävs för lokal källa',
      };
    }

    let config: Record<string, unknown>;
    if (type === 'mailbox') {
      config = { mailbox_email: form.mailboxEmail.trim() };
    } else if (type === 'local') {
      config = { directory_path: form.localDirectoryPath.trim() };
    } else {
      config = {
        host: form.sftpHost.trim(),
        port: Number(form.sftpPort || '22'),
        username: form.sftpUsername.trim(),
        password: form.sftpPassword.trim() || undefined,
        private_key_path: form.sftpPrivateKeyPath.trim() || undefined,
        passphrase: form.sftpPassphrase.trim() || undefined,
        base_path: form.sftpBasePath.trim(),
      };
    }

    const allowedExtensions = form.allowedExtensions
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    const filenameExclusions = form.filenameExclusions
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    const fileFilters: Record<string, unknown> = {};
    if (allowedExtensions.length > 0) fileFilters.allowed_extensions = allowedExtensions;
    if (filenameExclusions.length > 0) fileFilters.filename_exclusions = filenameExclusions;
    if (form.minDate.trim()) fileFilters.min_date = form.minDate.trim();
    if (form.pathPattern.trim()) fileFilters.path_pattern = form.pathPattern.trim();

    return {
      payload: {
        type,
        name,
        config,
        file_filters: fileFilters,
        retention_days: form.retentionDays.trim() ? Number(form.retentionDays.trim()) : null,
        is_active: form.isActive ? 1 : 0,
      },
    };
  };

  const handleSave = async () => {
    const { payload, error } = buildPayload() as
      | { payload: Record<string, unknown>; error?: undefined }
      | { payload?: undefined; error: string };

    if (error) {
      setFormError(error);
      return;
    }

    try {
      setSaving(true);
      setFormError(null);
      if (editingSourceId != null) {
        await api.put(`/api/admin/import-sources/${editingSourceId}`, payload);
        toast.success(t('sysAdmin.importSources.updated') || 'Importkälla uppdaterad');
      } else {
        await api.post('/api/admin/import-sources', payload);
        toast.success(t('sysAdmin.importSources.created') || 'Importkälla skapad');
      }
      setShowForm(false);
      resetForm();
      await loadSources();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t('sysAdmin.importSources.saveError') ||
        'Kunde inte spara importkälla';
      setFormError(String(message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (source: ImportSource) => {
    const confirmed = window.confirm(
      (t('sysAdmin.importSources.confirmDelete') || 'Radera denna importkälla?').replace(
        '{{name}}',
        source.name
      )
    );
    if (!confirmed) return;

    try {
      setDeletingSourceId(source.id);
      await api.delete(`/api/admin/import-sources/${source.id}`);
      toast.success(t('sysAdmin.importSources.deleted') || 'Importkälla raderad');
      await loadSources();
    } catch {
      toast.error(t('sysAdmin.importSources.deleteError') || 'Kunde inte radera importkälla');
    } finally {
      setDeletingSourceId(null);
    }
  };

  const handleTestConnection = async (source: ImportSource) => {
    try {
      setTestingSourceId(source.id);
      await api.post(`/api/admin/import-sources/${source.id}/test`);
      toast.success(t('sysAdmin.importSources.testSuccess') || 'Anslutning OK');
      await loadSources();
    } catch (error: unknown) {
      const errorData = (error as {
        response?: { data?: { error?: string; hint?: string; details?: { missingEnv?: string[] } } };
      })?.response?.data;
      const baseMessage =
        errorData?.error ||
        t('sysAdmin.importSources.testError') ||
        'Anslutningstest misslyckades';
      const missingEnv = errorData?.details?.missingEnv;
      const hint = errorData?.hint;
      const fullMessage =
        Array.isArray(missingEnv) && missingEnv.length > 0
          ? `${baseMessage}: ${missingEnv.join(', ')}`
          : hint
            ? `${baseMessage}. ${hint}`
            : baseMessage;
      toast.error(String(fullMessage), { duration: 8000 });
      await loadSources();
    } finally {
      setTestingSourceId(null);
    }
  };

  const handleSyncNow = async (source: ImportSource) => {
    try {
      setSyncingSourceId(source.id);
      const response = await api.post(`/api/admin/import-sources/${source.id}/sync`);
      const data = response?.data?.data;
      const imported = Number(data?.imported ?? 0);
      const skipped = Number(data?.skipped ?? 0);
      const totalFiles = Number(data?.totalFiles ?? data?.total_files ?? NaN);
      const totalPart = Number.isFinite(totalFiles)
        ? `, ${totalFiles} ${t('sysAdmin.importSources.totalFilesInSource') || 'filer i källan'}`
        : '';
      toast.success(
        `${t('sysAdmin.importSources.syncSuccess') || 'Sync klar'}: ${imported} nya, ${skipped} hoppade över${totalPart}`
      );
      await loadSources();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t('sysAdmin.importSources.syncError') ||
        'Sync misslyckades';
      toast.error(String(message));
      await loadSources();
    } finally {
      setSyncingSourceId(null);
    }
  };

  const statusLabel = (status?: SourceStatus) => {
    if (status === 'success') return t('imports.sourceHealthSuccess') || 'Lyckad';
    if (status === 'failed') return t('imports.sourceHealthFailed') || 'Misslyckad';
    return t('imports.sourceHealthUnknown') || 'Okänd';
  };

  const statusBadgeClass = (status?: SourceStatus) => {
    if (status === 'success') return 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30';
    if (status === 'failed') return 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30';
    return 'bg-bgElevated text-textSecondary border-border';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <PageBackButton />
            <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
              <ServerIcon className="w-6 h-6 text-primary" />
              {t('sysAdmin.importSources.title') || 'Importkällor'}
            </h1>
          </div>
          <p className="mt-2 text-sm text-textSecondary">
            {t('sysAdmin.importSources.description') ||
              'Hantera mailbox- och SFTP-källor för importsync'}
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90"
        >
          <PlusIcon className="w-5 h-5" />
          {t('sysAdmin.importSources.newSource') || 'Ny källa'}
        </button>
      </div>

      {showForm && (
        <div className="rounded-xl border border-border bg-bgElevated p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-textDefault">
              {editingSource
                ? t('sysAdmin.importSources.editSource') || 'Redigera källa'
                : t('sysAdmin.importSources.createSource') || 'Skapa källa'}
            </h2>
            <button
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="p-1 rounded hover:bg-bgBase"
            >
              <XMarkIcon className="w-5 h-5 text-textSecondary" />
            </button>
          </div>

          {formError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-textSecondary mb-1">
                {t('sysAdmin.importSources.type') || 'Typ'}
              </label>
              <select
                value={form.type}
                onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value as SourceType }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
              >
                <option value="mailbox">{t('sysAdmin.importSources.mailbox') || 'Mailbox'}</option>
                <option value="sftp">{t('sysAdmin.importSources.sftp') || 'SFTP'}</option>
                <option value="local">{t('sysAdmin.importSources.local') || 'Lokal katalog'}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">
                {t('sysAdmin.importSources.name') || 'Namn'}
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                placeholder={t('sysAdmin.importSources.namePlaceholder') || 'T.ex. Delad mailbox Circle K'}
              />
            </div>
          </div>

          {form.type === 'mailbox' ? (
            <div>
              <label className="block text-sm text-textSecondary mb-1">
                {t('sysAdmin.importSources.mailboxEmail') || 'Mailbox e-post'}
              </label>
              <input
                type="email"
                value={form.mailboxEmail}
                onChange={(e) => setForm((prev) => ({ ...prev, mailboxEmail: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                placeholder="shared@company.se"
              />
            </div>
          ) : form.type === 'local' ? (
            <div>
              <label className="block text-sm text-textSecondary mb-1">
                {t('sysAdmin.importSources.localDirectoryPath') || 'Katalogsökväg (på servern)'}
              </label>
              <input
                type="text"
                value={form.localDirectoryPath}
                onChange={(e) => setForm((prev) => ({ ...prev, localDirectoryPath: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                placeholder="/srv/sftp/rs-prod/DQFleetSFTPProd"
              />
              <p className="mt-1 text-xs text-textSecondary">
                {t('sysAdmin.importSources.localDirectoryHint') ||
                  'Absolut sökväg till katalogen som backend läser filer från (t.ex. en SFTP-upload-katalog).'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.importSources.sftpHost') || 'Host'}
                </label>
                <input
                  type="text"
                  value={form.sftpHost}
                  onChange={(e) => setForm((prev) => ({ ...prev, sftpHost: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.importSources.sftpPort') || 'Port'}
                </label>
                <input
                  type="number"
                  value={form.sftpPort}
                  onChange={(e) => setForm((prev) => ({ ...prev, sftpPort: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.importSources.sftpUsername') || 'Användarnamn'}
                </label>
                <input
                  type="text"
                  value={form.sftpUsername}
                  onChange={(e) => setForm((prev) => ({ ...prev, sftpUsername: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.importSources.sftpPassword') || 'Lösenord (valfritt)'}
                </label>
                <input
                  type="password"
                  value={form.sftpPassword}
                  onChange={(e) => setForm((prev) => ({ ...prev, sftpPassword: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.importSources.sftpPrivateKeyPath') || 'Private key path (valfritt)'}
                </label>
                <input
                  type="text"
                  value={form.sftpPrivateKeyPath}
                  onChange={(e) => setForm((prev) => ({ ...prev, sftpPrivateKeyPath: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.importSources.sftpPassphrase') || 'Lösenfras för nyckel (valfritt)'}
                </label>
                <input
                  type="password"
                  value={form.sftpPassphrase}
                  onChange={(e) => setForm((prev) => ({ ...prev, sftpPassphrase: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.importSources.sftpBasePath') || 'Base path'}
                </label>
                <input
                  type="text"
                  value={form.sftpBasePath}
                  onChange={(e) => setForm((prev) => ({ ...prev, sftpBasePath: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                  placeholder="/incoming"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-textSecondary mb-1">
                {t('sysAdmin.importSources.allowedExtensions') || 'Tillåtna filändelser (kommaseparerat)'}
              </label>
              <input
                type="text"
                value={form.allowedExtensions}
                onChange={(e) => setForm((prev) => ({ ...prev, allowedExtensions: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                placeholder=".csv, .xlsx, .dat"
              />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">
                {t('sysAdmin.importSources.filenameExclusions') ||
                  'Filnamn att exkludera (kommaseparerat)'}
              </label>
              <input
                type="text"
                value={form.filenameExclusions}
                onChange={(e) => setForm((prev) => ({ ...prev, filenameExclusions: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                placeholder="tmp, bak"
              />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">
                {t('sysAdmin.importSources.minDate') || 'Min datum (mailbox, YYYY-MM-DD)'}
              </label>
              <input
                type="text"
                value={form.minDate}
                onChange={(e) => setForm((prev) => ({ ...prev, minDate: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                placeholder="2026-02-01"
              />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">
                {t('sysAdmin.importSources.pathPattern') || 'Path pattern (SFTP)'}
              </label>
              <input
                type="text"
                value={form.pathPattern}
                onChange={(e) => setForm((prev) => ({ ...prev, pathPattern: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
                placeholder="*.csv"
              />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">
                {t('sysAdmin.importSources.retentionDays') || 'Retention (dagar)'}
              </label>
              <input
                type="number"
                min={0}
                value={form.retentionDays}
                onChange={(e) => setForm((prev) => ({ ...prev, retentionDays: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgBase text-textDefault"
              />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm text-textDefault">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded border-border"
                />
                {t('sysAdmin.importSources.isActive') || 'Aktiv källa'}
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="px-4 py-2 rounded-lg border border-border text-textDefault"
            >
              {t('common.cancel') || 'Avbryt'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white disabled:opacity-60"
            >
              {saving && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
              {editingSource
                ? t('sysAdmin.importSources.saveChanges') || 'Spara ändringar'
                : t('sysAdmin.importSources.create') || 'Skapa'}
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-bgElevated overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold text-textDefault">
            {t('sysAdmin.importSources.sourcesList') || 'Importkällor'}
          </h2>
          <button
            onClick={loadSources}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border text-textDefault"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh') || 'Uppdatera'}
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-textSecondary">{t('common.loading') || 'Laddar...'}</div>
        ) : sources.length === 0 ? (
          <div className="p-6 text-sm text-textSecondary">
            {t('sysAdmin.importSources.noSources') || 'Inga importkällor skapade ännu'}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sources.map((source) => (
              <div key={source.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold text-textDefault">{source.name}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full border border-border text-textSecondary">
                        {source.type}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${statusBadgeClass(
                          source.last_fetch_status ?? null
                        )}`}
                      >
                        {statusLabel(source.last_fetch_status ?? null)}
                      </span>
                      {!source.is_active && (
                        <span className="text-xs px-2 py-0.5 rounded-full border border-border text-textSecondary">
                          {t('sysAdmin.importSources.inactive') || 'Inaktiv'}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-textSecondary">
                      {t('sysAdmin.importSources.lastFetchAt') || 'Senaste hämtning'}:{' '}
                      {source.last_fetch_at
                        ? new Date(source.last_fetch_at).toLocaleString('sv-SE')
                        : t('imports.notFetchedYet') || 'Aldrig'}
                    </div>
                    <div className="text-sm text-textSecondary">
                      {t('sysAdmin.importSources.lastFetchFilesCount') || 'Antal filer senaste sync'}:{' '}
                      {source.last_fetch_files_count ?? 0}
                    </div>
                    {source.last_fetch_error && (
                      <div className="text-sm text-red-600 dark:text-red-400">
                        {t('sysAdmin.importSources.lastFetchError') || 'Senaste fel'}: {source.last_fetch_error}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSyncNow(source)}
                      disabled={syncingSourceId === source.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-textDefault disabled:opacity-60"
                    >
                      {syncingSourceId === source.id ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        <ArrowPathIcon className="w-4 h-4" />
                      )}
                      {t('sysAdmin.importSources.syncNow') || 'Sync nu'}
                    </button>
                    <button
                      onClick={() => handleTestConnection(source)}
                      disabled={testingSourceId === source.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-textDefault disabled:opacity-60"
                    >
                      {testingSourceId === source.id ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        <WifiIcon className="w-4 h-4" />
                      )}
                      {t('sysAdmin.importSources.testConnection') || 'Testa anslutning'}
                    </button>
                    <button
                      onClick={() => populateFormForEdit(source)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-textDefault"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                      {t('common.edit') || 'Redigera'}
                    </button>
                    <button
                      onClick={() => handleDelete(source)}
                      disabled={deletingSourceId === source.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/40 text-red-600 dark:text-red-400 disabled:opacity-60"
                    >
                      {deletingSourceId === source.id ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        <TrashIcon className="w-4 h-4" />
                      )}
                      {t('common.delete') || 'Radera'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportSourcesManagement;
